import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { getSalatProduct } from "../../api";
import { api_url } from "../../constant";
import { useCart } from "../../store/cartProvider";
import ConvertToArabicNumbers from "../../utils/convert_numbers";
import empty_box from "../../images/empty_box.png";

function SalatCart() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const cart = useCart();
  const navigate = useNavigate();
  const warnedRef = useRef(new Set());

  const results = useQueries({
    queries: cart.items.map((item) => ({
      queryKey: ["salatProduct", item.productId],
      queryFn: () => getSalatProduct(item.productId),
      retry: false,
    })),
  });

  useEffect(() => {
    results.forEach((result, index) => {
      const item = cart.items[index];
      if (!item) return;
      if (result.isError && !warnedRef.current.has(item.productId)) {
        warnedRef.current.add(item.productId);
        toast.warn(t("salatMahsool.cart.unavailableRemoved", { name: `#${item.productId}` }));
        cart.removeItem(item.productId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const lines = cart.items
    .map((item, index) => ({ item, product: results[index]?.data }))
    .filter((line) => line.product);

  const isFetching = results.some((r) => r.isLoading);

  const formatMoney = (value) => {
    const formatted = value.toFixed(2);
    return isRtl ? ConvertToArabicNumbers(formatted) : formatted;
  };

  const total = lines.reduce(
    (sum, line) => sum + parseFloat(line.product.price) * line.item.quantity,
    0
  );

  const handleQuantityChange = (line, value) => {
    let quantity = parseInt(value, 10);
    if (isNaN(quantity) || quantity < 1) quantity = 1;
    const max = line.product.quantity != null ? line.product.quantity : null;
    cart.updateQuantity(line.item.productId, quantity, max);
  };

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="w-full lg:px-20 px-4 py-8">
      <Helmet>
        <title>{t("salatMahsool.cart.title")} - Mahsool</title>
      </Helmet>

      <h1 className="font-bold text-2xl lg:text-3xl mb-8 text-primary">
        {t("salatMahsool.cart.title")}
      </h1>

      {!isFetching && lines.length === 0 ? (
        <div className="flex flex-col items-center mt-10 text-center">
          <img src={empty_box} alt="" className="w-40 lg:w-48" />
          <div className="font-bold mt-3 text-lg text-gray-700">{t("salatMahsool.cart.empty")}</div>
          <Link to="/home/salatMahsool" className="mt-4 text-primary underline">
            {t("salatMahsool.cart.browseCatalog")}
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-4 max-w-3xl">
          {lines.map((line) => {
            const name = i18n.language === "en" && line.product.name_en ? line.product.name_en : line.product.name;
            const subtotal = parseFloat(line.product.price) * line.item.quantity;
            return (
              <div
                key={line.item.productId}
                className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm"
              >
                <img
                  src={line.product.images?.[0] ? `${api_url}/${line.product.images[0]}` : empty_box}
                  alt={name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <div className="font-bold text-gray-800">{name}</div>
                  <div className="text-sm text-gray-500">
                    {formatMoney(parseFloat(line.product.price))} {t("common.currency")}
                  </div>
                </div>
                <input
                  type="number"
                  min={1}
                  max={line.product.quantity != null ? line.product.quantity : undefined}
                  value={line.item.quantity}
                  onChange={(e) => handleQuantityChange(line, e.target.value)}
                  className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-center"
                />
                <div className="font-semibold text-primary w-24 text-center">
                  {formatMoney(subtotal)} {t("common.currency")}
                </div>
                <button
                  onClick={() => cart.removeItem(line.item.productId)}
                  className="text-red-500 hover:text-red-700 px-2"
                >
                  {t("salatMahsool.cart.remove")}
                </button>
              </div>
            );
          })}

          <div className="flex flex-col items-end gap-1 mt-4 border-t pt-4">
            <div className="text-xl font-bold text-primary">
              {t("salatMahsool.cart.estimatedTotal")}: {formatMoney(total)} {t("common.currency")}
            </div>
            <div className="text-sm text-gray-500">{t("salatMahsool.cart.estimateNote")}</div>
            <button
              onClick={() => navigate("/home/salatMahsool/checkout")}
              className="mt-4 px-6 py-3 text-lg font-bold text-white bg-primary rounded-lg shadow-md hover:bg-primary-dark hover:shadow-lg"
            >
              {t("salatMahsool.cart.checkout")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalatCart;
